import { ChangeDetectionStrategy, Component, effect, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MdwTabGroup } from '@spaces-ui/meadow/components/tab-group';
import { Time } from '@spaces-ui/utils';

import { MdwTwoDigitNumber } from '../time-picker/two-digit-number';

@Component({
  selector: 'mdw-time-input',
  templateUrl: './time-input.html',
  styleUrls: ['./time-input.scss'],
  imports: [FormsModule, MdwTwoDigitNumber, MdwTabGroup],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.size-small]': "size() === 'small'",
  },
})
export class MdwTimeInput {
  public value = model<Time>();
  public size = input<'small' | 'medium'>('medium');
  public showLabel = input<boolean>(true);

  public hours = signal<string | undefined>(undefined);
  public minutes = signal<string | undefined>(undefined);
  public isAm = signal<boolean>(true);
  public isInitialized = signal<boolean>(false);

  public constructor() {
    effect(() => {
      const value = this.value();
      const isInitialized = this.isInitialized();

      if (isInitialized) {
        return;
      }

      let initialHour: number | undefined;
      let isAm = true;
      if (value?.hours) {
        isAm = value.hours < 12;
        initialHour = isAm ? value.hours : value.hours - 12;
      }

      this.hours.set(initialHour ? `${initialHour}` : undefined);
      this.minutes.set(
        value?.minutes
          ? value.minutes < 10
            ? `0${value.minutes}`
            : `${value.minutes}`
          : undefined,
      );
      this.isAm.set(isAm);

      this.isInitialized.set(true);
    });
  }

  public updateTime(newHours?: string, newMinutes?: string) {
    const hourString = newHours ?? this.hours();
    const minString = newMinutes ?? this.minutes();

    let hours = hourString ? parseInt(hourString, 10) : 0;
    const minutes = minString ? parseInt(minString, 10) : 0;
    const isAm = this.isAm() ?? true;

    hours = hours === 12 ? 0 : (hours ?? 0);
    if (!isAm) {
      hours += 12;
    }

    const time: Time = {
      hours: hours ?? 0,
      minutes: minutes,
      seconds: 0,
    };

    if (newHours) {
      this.hours.set(newHours);
    }

    if (newMinutes) {
      this.minutes.set(newMinutes);
    }

    this.value.set(time);
  }

  public handleToggleAmPm(value: string) {
    this.isAm.set(value === 'AM');
    this.updateTime();
  }

  public handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }
}
